import { Expand01Icon, Minimize01Icon } from '@langchain/untitled-ui-icons';

import {
  LucideBinary,
  LucideBraces,
  LucideMessageCircle,
  LucideToggleLeft,
  LucideType,
} from 'lucide-react';
import * as React from 'react';

import { CodeData } from '@/components/Code/Code';
import { EditableChatInput } from '@/components/EditableChatInput';
import { EditableMessage } from '@/components/EditableMessage';
import { PlainTextEditor } from '@/components/PlainTextEditor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Select/Select';
import { Tooltip } from '@/design-system';
import {
  MessageCapability,
  MessageFields,
  StoredMessage,
} from '@/types/schema';

import { Message } from '../../../data/message';
import { MessageViewInner } from '../trace/message';
import { MultiNumberArray } from './MultiNumberArray';
import { MultiStringArray } from './MultiStringArray';
import { SimpleCard } from './card';
import { useExpandCollapse } from './expandCollapseContext';
import { SchemaRenderer } from './schema-editor';

export const defaultSchemaRenderer: SchemaRenderer = {
  StringArray: (props) => {
    const options =
      typeof props.schema === 'object' &&
      props.schema.items !== undefined &&
      typeof props.schema.items === 'object' &&
      'enum' in props.schema.items &&
      Array.isArray(props.schema.items.enum)
        ? props.schema.items.enum.filter(
            (x): x is string => typeof x === 'string'
          )
        : [];

    return (
      <SimpleCard
        title={
          <span className="flex items-center gap-2 font-medium capitalize">
            <LucideBraces className="inline-block stroke-1 text-tertiary" />
            <span>{props.name.replaceAll('_', ' ').replaceAll('-', ' ')}</span>
          </span>
        }
        required={props.required}
        open={props.openByDefault}
        hideRequiredLabel={props.options?.hideRequiredLabels}
        onDeleteKey={props.value !== undefined ? props.onDeleteKey : undefined}
      >
        <MultiStringArray
          value={props.value}
          onChange={props.onChange}
          enum={options}
        />
      </SimpleCard>
    );
  },
  String: (props) => {
    let content = (
      <PlainTextEditor
        autoFocus={!props.options?.disableAutoFocus}
        readOnly={props.readOnly}
        className="w-full rounded-lg border border-secondary bg-primary p-3 transition-colors focus-within:border-brand"
        textareaClassName="text-sm"
        placeholder="Input"
        value={props.value}
        onChange={(value) => props.onChange(value)}
      />
    );

    // https://react.dev/errors/31?invariant=31&args%5B%5D=object%2520with%2520keys%2520%257Bcontent%252C%2520additional_kwargs%252C%2520response_metadata%252C%2520type%252C%2520name%252C%2520id%252C%2520example%252C%2520tool_calls%252C%2520invalid_tool_calls%252C%2520usage_metadata%257D
    if (typeof props.value !== 'string' && props.value != null) {
      content = (
        <CodeData
          value={props.value}
          onChange={props.onChange}
          language="json"
        />
      );
    }

    if (
      typeof props.schema === 'object' &&
      'enum' in props.schema &&
      Array.isArray(props.schema.enum) &&
      props.schema.enum.length > 0
    ) {
      content = (
        <Select value={props.value} onValueChange={props.onChange}>
          <SelectTrigger>
            <SelectValue placeholder={props.name} />
          </SelectTrigger>
          <SelectContent>
            {props.schema.enum
              .filter((x): x is string => typeof x === 'string')
              .map((value: string, i) => (
                <SelectItem key={i} value={value}>
                  {value}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      );
    }

    return (
      <SimpleCard
        title={
          <span className="flex items-center gap-2 font-medium capitalize">
            <LucideType className="inline-block size-5 stroke-[2px] text-tertiary" />{' '}
            <span>{props.name.replaceAll('_', ' ').replaceAll('-', ' ')}</span>
          </span>
        }
        required={props.required}
        open={props.openByDefault}
        hideRequiredLabel={props.options?.hideRequiredLabels}
        onDeleteKey={props.value !== undefined ? props.onDeleteKey : undefined}
      >
        {content}
      </SimpleCard>
    );
  },
  Number: (props) => (
    <SimpleCard
      title={
        <span className="flex items-center gap-2 font-medium capitalize">
          <LucideBinary className="inline-block size-5 stroke-[2px] text-tertiary" />{' '}
          <span>{props.name.replaceAll('_', ' ').replaceAll('-', ' ')}</span>
        </span>
      }
      required={props.required}
      open={props.openByDefault}
      hideRequiredLabel={props.options?.hideRequiredLabels}
      onDeleteKey={props.value !== undefined ? props.onDeleteKey : undefined}
    >
      <input
        autoFocus={!props.options?.disableAutoFocus}
        disabled={props.readOnly}
        className="w-full rounded-lg border border-secondary bg-background p-3 transition-colors focus-within:border-brand"
        placeholder="Input"
        type="number"
        value={
          typeof props.value !== 'number' || Number.isNaN(props.value)
            ? ''
            : props.value
        }
        onChange={(e) => props.onChange(e.target.valueAsNumber)}
      />
    </SimpleCard>
  ),
  NumberArray: (props) => {
    const options =
      typeof props.schema === 'object' &&
      props.schema.items !== undefined &&
      typeof props.schema.items === 'object' &&
      'enum' in props.schema.items &&
      Array.isArray(props.schema.items.enum)
        ? props.schema.items.enum.filter(
            (x): x is number => typeof x === 'number'
          )
        : [];

    return (
      <SimpleCard
        title={
          <span className="flex items-center gap-2 font-medium capitalize">
            <LucideBraces className="inline-block size-5 stroke-[2px] text-tertiary" />
            <span>{props.name.replaceAll('_', ' ').replaceAll('-', ' ')}</span>
          </span>
        }
        required={props.required}
        open={props.openByDefault}
        hideRequiredLabel={props.options?.hideRequiredLabels}
        onDeleteKey={props.value !== undefined ? props.onDeleteKey : undefined}
      >
        <MultiNumberArray
          disableAutoFocus={props.options?.disableAutoFocus}
          value={props.value}
          onChange={props.onChange}
          enum={options}
        />
      </SimpleCard>
    );
  },
  Boolean: (props) => (
    <SimpleCard
      title={
        <span className="flex items-center gap-2 font-medium capitalize">
          <LucideToggleLeft className="inline-block size-5 stroke-[2px] text-tertiary" />{' '}
          <span>{props.name.replaceAll('_', ' ').replaceAll('-', ' ')}</span>
        </span>
      }
      required={props.required}
      open={props.openByDefault}
      hideRequiredLabel={props.options?.hideRequiredLabels}
      onDeleteKey={props.value !== undefined ? props.onDeleteKey : undefined}
    >
      <input
        disabled={props.readOnly}
        type="checkbox"
        checked={props.value}
        onChange={(e) => props.onChange(e.target.checked)}
      />
    </SimpleCard>
  ),
  Message: (props) => (
    <SimpleCard
      open={true}
      title={
        <span className="flex items-center gap-2 font-medium capitalize">
          <LucideMessageCircle className="inline-block size-5 stroke-[2px] text-tertiary" />{' '}
          <span>{props.name.replaceAll('_', ' ').replaceAll('-', ' ')}</span>
        </span>
      }
      required={props.required}
      hideRequiredLabel={props.options?.hideRequiredLabels}
      onDeleteKey={props.value !== undefined ? props.onDeleteKey : undefined}
    >
      {props.readOnly ? (
        <MessageViewInner message={props.value} />
      ) : (
        <EditableMessage
          messageValue={{
            type: props.value.type,
            data: props.value as MessageFields,
          }}
          onChange={(value) => {
            const { type, data } = value as StoredMessage;
            props.onChange({ ...data, type } as Message);
          }}
          onDelete={() => props.onChange(null)}
        />
      )}
    </SimpleCard>
  ),
  MessageArray: (props) => {
    const value: Message[] = Array.isArray(props.value)
      ? props.value
      : [props.value];
    return (
      <SimpleCard
        open={true}
        title={
          <span className="flex items-center gap-2 font-medium capitalize">
            <LucideMessageCircle className="inline-block size-5 stroke-[2px] text-tertiary" />{' '}
            <span>{props.name.replaceAll('_', ' ').replaceAll('-', ' ')}</span>
          </span>
        }
        required={props.required}
        hideRequiredLabel={props.options?.hideRequiredLabels}
        onDeleteKey={
          props.value !== undefined && value.length > 0
            ? props.onDeleteKey
            : undefined
        }
      >
        <div>
          <EditableChatInput
            className="h-auto"
            disableCollapse
            messages={value.map((item) => {
              const data = { ...item } as MessageFields;

              // In the @langchain/openai JS library, if tool_calls are present in additional_kwargs,
              // they take precedence over tool_calls in the message itself.
              // However, in the `langchain-openai` Python library, the tool_calls in the
              // message itself take precedence instead.
              // So, we need to decide if we want to use LC tool_calls or OAI tools in additional_kwargs.
              // TODO: move to <Message> component
              if (
                'additional_kwargs' in data &&
                typeof data.additional_kwargs === 'object' &&
                data.additional_kwargs != null &&
                'tool_calls' in data.additional_kwargs &&
                Array.isArray(data.additional_kwargs.tool_calls)
              ) {
                const unifiedMessageIds = [
                  ...(data.tool_calls ?? []),
                  ...(data.invalid_tool_calls ?? []),
                ].map((i) => i.id);

                const oaiMessageIds = data.additional_kwargs.tool_calls.map(
                  (i) => i.id
                );

                if (
                  oaiMessageIds.length === unifiedMessageIds.length &&
                  oaiMessageIds.every((v) => unifiedMessageIds.includes(v))
                ) {
                  delete data.additional_kwargs.tool_calls;
                } else {
                  delete data.tool_calls;
                  delete data.invalid_tool_calls;
                }
              }

              return { type: item.type, data };
            })}
            setMessages={(messages) => {
              props.onChange(
                messages.map((item) => {
                  const { type, data } = item as StoredMessage;
                  return { ...data, type: type ?? 'human' } as Message;
                })
              );
            }}
            readOnly={props.readOnly}
            capabilities={[MessageCapability.IMAGE]}
            variant={'inline'}
            imageVariant={'inline'}
          />
        </div>
      </SimpleCard>
    );
  },
  MessageRecord: (props) => {
    const [key, setKey] = React.useState('');
    return (
      <SimpleCard
        title={
          <span className="flex items-center gap-2 font-medium capitalize">
            <LucideMessageCircle className="inline-block size-5 stroke-[2px] text-tertiary" />
            <span>{props.name.replaceAll('_', ' ').replaceAll('-', ' ')}</span>
          </span>
        }
        required={props.required}
        open={props.openByDefault}
        hideRequiredLabel={props.options?.hideRequiredLabels}
        onDeleteKey={props.value !== undefined ? props.onDeleteKey : undefined}
      >
        {Object.keys(props.value).length === 0 && (
          <div className="flex min-w-0 flex-col gap-1">No messages</div>
        )}
        {Object.entries(props.value).map(([k, msg], i) => (
          <div key={i}>
            {defaultSchemaRenderer.Message({
              readOnly: props.readOnly ?? false,
              name: `${props.name}[${k}]`,
              value: msg,
              schema: props.schema,
              options: props.options,
              onChange: (v) => {
                if (v == null) {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { [k]: _, ...rest } = props.value;
                  props.onChange(rest);
                } else {
                  props.onChange({ ...props.value, [k]: v });
                }
              },
            })}
          </div>
        ))}
        {!props.readOnly && (
          <div className="flex w-full place-items-baseline gap-2">
            <input
              className="rounded p-2 text-xs "
              type="text"
              placeholder="Add key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
            <button
              type="button"
              className="mt-2 rounded p-2 text-xs"
              onClick={() =>
                props.onChange({
                  ...props.value,
                  [key]: { type: 'human', content: '' },
                })
              }
            >
              Add
            </button>
          </div>
        )}
      </SimpleCard>
    );
  },
  Default: (props) => {
    return (
      <SimpleCard
        title={
          <span className="flex items-center gap-2 font-medium capitalize">
            <LucideBraces className="inline-block size-5 stroke-[2px] text-tertiary" />
            <span>{props.name.replaceAll('_', ' ').replaceAll('-', ' ')}</span>
          </span>
        }
        required={props.required}
        open={props.openByDefault}
        hideRequiredLabel={props.options?.hideRequiredLabels}
        onDeleteKey={props.exists ? props.onDeleteKey : undefined}
      >
        <div className="rounded-md border border-secondary">
          <CodeData
            value={props.value}
            onChange={props.onChange}
            autoFocus={!props.options?.disableAutoFocus}
          />
        </div>
      </SimpleCard>
    );
  },
  Wrapper: (props) => {
    const expand = useExpandCollapse();
    if (!expand) {
      return <>{props.children}</>;
    }
    const allExpanded = expand.areAllExpanded();

    return (
      <div className="relative flex flex-col">
        <Tooltip title={allExpanded ? 'Collapse all' : 'Expand all'}>
          <button
            type="button"
            onClick={() =>
              allExpanded ? expand.collapseAll() : expand.expandAll()
            }
            className="absolute -top-10 right-0 cursor-pointer self-end rounded-md p-1 hover:bg-secondary"
          >
            {allExpanded ? (
              <Minimize01Icon className="h-4 w-4" />
            ) : (
              <Expand01Icon className="h-4 w-4" />
            )}
          </button>
        </Tooltip>
        {props.children}
      </div>
    );
  },
  Object: (props) => (
    <div className="relative">
      <SimpleCard
        title={
          <span className="flex items-center gap-2 font-medium capitalize">
            <LucideBraces className="inline-block size-5 stroke-[2px] text-tertiary" />
            <span>{props.name.replaceAll('_', ' ').replaceAll('-', ' ')}</span>
          </span>
        }
        required={props.required}
        open={props.openByDefault}
        hideRequiredLabel={props.options?.hideRequiredLabels}
        onDeleteKey={props.value !== undefined ? props.onDeleteKey : undefined}
      >
        <div className="relative ml-[11px] border-l border-primary pl-4">
          {React.Children.map(props.children, (child, index) => (
            <div key={index} className="relative">
              <div className="absolute -left-4 top-4 h-px w-4 bg-secondary" />
              {child}
            </div>
          ))}
        </div>
      </SimpleCard>
    </div>
  ),
};
